/* DeleteAction.js */

"use strict";

const deleteSiteUtility = require("../../deleteSiteUtility.js");

class DeleteAction {
    constructor(logger, gameEditor, modalUserFeedback, activeView, userSession, SiteEntity) {
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._modalUserFeedback = modalUserFeedback;
        this._activeView = activeView;
        this._activeOrgId = userSession.activeOrganization.id;
        this._SiteEntity = SiteEntity;
        this._selectedSiteIds = [];

        if (this._activeView && this._activeView.pagingFeed) {
            this._selectedSiteIds = this._activeView.getSelectedSiteIds().selectedRows || [];
        }

        this.run = this.run.bind(this);
    }

    run() {
        let siteIds = [...this._selectedSiteIds];
        if (!siteIds.length) {
            return Promise.resolve();
        }

        const isRootSite = siteIds.find((id) => id === this._activeOrgId);
        const nonRootSiteIds = siteIds.filter((id) => id !== this._activeOrgId);
        let expandedIds = [];
        let hasChildSite = false;

        return this._activeView.pagingFeed
            .waitForRangeIdsUpdated()
            .then(() => {
                if (nonRootSiteIds.length === 0 && isRootSite) {
                    return Promise.reject({
                        title: "Delete Sites",
                        msg: deleteSiteUtility.ROOT_SITE_MESSAGE,
                    });
                }
                return deleteSiteUtility.expandSiteIds(
                    this._SiteEntity,
                    this._activeOrgId,
                    nonRootSiteIds
                );
            })
            .then((ids) => {
                expandedIds = ids;
                return deleteSiteUtility.checkDeleteBlockers(
                    this._SiteEntity,
                    this._activeOrgId,
                    expandedIds
                );
            })
            .then((blockers) => {
                if (blockers.hasDeviceSite) {
                    return Promise.reject({
                        title: "Delete Sites",
                        msg: isRootSite
                            ? deleteSiteUtility.ROOT_SITE_MESSAGE
                            : deleteSiteUtility.buildDeviceSiteMessage(blockers.hasDeviceSiteCount),
                    });
                }
                hasChildSite = blockers.hasChildSite;
                if (expandedIds.length <= deleteSiteUtility.SITE_NAME_LIST_LENGTH) {
                    return this._activeView.pagingFeed.getSiteEntitiesNameById(expandedIds);
                }
                return Promise.resolve();
            })
            .then((selectedSiteNames) => {
                const message = deleteSiteUtility.buildDeleteMessage({
                    expandedIds,
                    siteNames: selectedSiteNames,
                    hasChildSite,
                    isRootSite,
                });

                return this._gameEditor.runQuickConfirmationActionWorkflow(
                    {
                        message,
                        title: "Delete Sites",
                        confirmDetails: {
                            text: "Confirm",
                            type: "destructive",
                        },
                        rejectDetails: {
                            text: "Cancel",
                            type: "text",
                        },
                    },
                    () => {
                        return this._SiteEntity
                            .runSiteDeleteWork(
                                this._activeOrgId,
                                deleteSiteUtility.buildWorkTarget(expandedIds),
                                {},
                                {launchAndForget: false},
                                deleteSiteUtility.makeProgressCallback(this._modalUserFeedback)
                            )
                            .then((workResults) => {
                                this._SiteEntity.throwWorkErrors(workResults);
                                this._activeView.resetSelection();
                                if (
                                    this._activeView &&
                                    this._activeView.pagingFeed &&
                                    this._activeView.pagingFeed.feedUpdate
                                ) {
                                    this._activeView.pagingFeed.feedUpdate();
                                }
                                return Promise.resolve();
                            })
                            .catch((err) => {
                                if (
                                    err.message &&
                                    err.message.search("EntityInteractionsError") !== -1
                                ) {
                                    return this._modalUserFeedback
                                        .displayError({
                                            error: {
                                                errorMsg:
                                                    "Sorry, you can’t delete a site that has devices assigned to it. Remove the devices from the site and any child site.",
                                            },
                                        })
                                        .catch(() => {
                                            return Promise.resolve();
                                        });
                                }
                                return Promise.reject(err);
                            });
                    }
                );
            })
            .catch((err) => {
                this._logger.warn({error: err}, "DeleteAction.run() - Error");
                return this._modalUserFeedback.displayError(
                    err.msg
                        ? {
                              errorMsg: err.msg,
                              errorCode: "",
                              errorTitle: err.title,
                              title: err.title,
                          }
                        : err
                );
            });
    }
}

module.exports = DeleteAction;

DeleteAction.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.ui.userFeedback",
    "gameEditor.activeView",
    "$teve.userSession",
    "$teve.entities.Site",
];

DeleteAction.$displayDetails = {
    type: "Action",
    name: "DeleteAction",
    icon: "common.trash_bin",
    shown: true,
    title: "Delete site",
    order: 15,
};
